/**
 * Price Bar Component
 * Compact horizontal bar with clickable asset pills showing price + CVD
 */

import { useMemo, useRef, useEffect, useState } from 'react';
import { TrendingUp, TrendingDown } from 'lucide-react';
import { useAlertStore, getFlatPrice, getNetCVD, type Asset } from '@/stores/alert-store';
import { formatPrice, formatCompact, cn } from '@/lib/utils';

const ASSETS: readonly Asset[] = ['BTC', 'ETH', 'SOL', 'HYPE', 'LIT'] as const;

interface AssetPillProps {
  readonly asset: Asset;
  readonly selected: boolean;
  readonly onClick: () => void;
}

function AssetPill({ asset, selected, onClick }: AssetPillProps) {
  const { prices, cvd } = useAlertStore();
  const [flash, setFlash] = useState<'up' | 'down' | null>(null);
  const prevPriceRef = useRef<number>(0);

  const price = useMemo(() => getFlatPrice(prices[asset]), [prices, asset]);
  const netCVD = useMemo(() => getNetCVD(cvd[asset]), [cvd, asset]);

  useEffect(() => {
    if (prevPriceRef.current !== 0 && price !== prevPriceRef.current) {
      setFlash(price > prevPriceRef.current ? 'up' : 'down');
      const timeout = setTimeout(() => setFlash(null), 300);
      return () => clearTimeout(timeout);
    }
    prevPriceRef.current = price;
  }, [price]);

  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'flex items-center gap-2 rounded-md px-3 py-1.5 text-sm font-medium transition-all',
        'border border-transparent',
        selected
          ? 'border-primary bg-primary/10 text-primary'
          : 'text-foreground hover:bg-accent',
        flash === 'up' && 'bg-green-500/10',
        flash === 'down' && 'bg-red-500/10'
      )}
    >
      <span className="font-bold">{asset}</span>
      <span className="tabular-nums text-xs">
        {price > 0 ? formatPrice(price) : '--'}
      </span>
      {netCVD !== 0 && (
        <span
          className={cn(
            'flex items-center gap-0.5 text-xs tabular-nums',
            netCVD > 0 ? 'text-green-500' : 'text-red-500'
          )}
        >
          {netCVD > 0 ? (
            <TrendingUp className="h-3 w-3" />
          ) : (
            <TrendingDown className="h-3 w-3" />
          )}
          {formatCompact(Math.abs(netCVD))}
        </span>
      )}
    </button>
  );
}

export function PriceBar() {
  const { selectedAsset, setSelectedAsset } = useAlertStore();

  return (
    <div className="flex items-center gap-1 overflow-x-auto border-b border-border bg-card px-2 py-1">
      {ASSETS.map((asset) => (
        <AssetPill
          key={asset}
          asset={asset}
          selected={selectedAsset === asset}
          onClick={() => setSelectedAsset(asset)}
        />
      ))}
    </div>
  );
}
